import { useApi } from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useState } from 'react';
import {
  customersApiRef,
  type GetOrCreateCustomerResult,
} from '../../api/customersApi';

interface RegisterCustomerDialogProps {
  initialName: string;
  onRegistered: (customer: GetOrCreateCustomerResult) => void;
  onClose: () => void;
}

export const RegisterCustomerDialog = ({
  initialName,
  onRegistered,
  onClose,
}: RegisterCustomerDialogProps) => {
  const api = useApi(customersApiRef);
  const [name, setName] = useState(initialName);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [result, setResult] = useState<GetOrCreateCustomerResult | undefined>();

  const handleRegister = () => {
    setRegistering(true);
    setError(undefined);
    api
      .getOrCreateCustomer(name.trim())
      .then(customer => {
        setResult(customer);
        onRegistered(customer);
      })
      .catch(setError)
      .finally(() => setRegistering(false));
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Register a customer</DialogTitle>
      <DialogContent>
        <TextField
          label="Customer name"
          fullWidth
          value={name}
          disabled={registering || Boolean(result)}
          error={Boolean(error)}
          helperText={error ? 'Unable to register customer.' : undefined}
          onChange={event => setName(event.target.value)}
        />
        {result && (
          <Typography variant="body2" style={{ marginTop: 16 }}>
            <strong>{result.name}</strong> — code <strong>{result.code}</strong>{' '}
            (
            {result.alreadyExists
              ? 'customer already existed'
              : 'customer newly created'}
            )
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            color="primary"
            variant="contained"
            disabled={registering || !name.trim()}
            onClick={handleRegister}
            startIcon={registering ? <CircularProgress size={16} /> : undefined}
          >
            Register
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
